import React, {useCallback, useState} from "react";
import {observer} from "mobx-react-lite";
import {IconButton, InputAdornment, Skeleton, Stack, TextField} from "@mui/material";
import StoreIcon from "~/icons/svg/StoreIcon.svg?react";
import DbIcon from "~/icons/svg/DbIcon.svg?react";
import DollarCoinIcon from "~/icons/svg/DollarCoinIcon.svg?react";
// import VerifiedIcon from "~/icons/svg/VerifiedIcon.svg?react";
import SearchIcon from "~/icons/svg/SearchIcon.svg?react";
import CloseIcon from "~/icons/svg/CloseIcon.svg?react";
import {generatePath, useNavigate, useParams} from "react-router";
import {useDataGridQueryState} from "~/components/data-grid/useDataGridQueryState";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent";
import {StatChip} from "~/components/StatChip/StatChip";
import {SegmentedControl} from "~/components/segmented-control/SegmentedControl";
import {COLORS} from "~/style/colors-ts";
import {useStores} from "~/stores";
import {useDebouncedSearch} from "~/hooks/useDebouncedSearch";
import {routes} from "~/routing/routes";
import type {DatabagRouteMatch} from "~/routing/catRoutes";
import {entityListParamsFromFilterModel} from "./components/normalizedTable/supplierEntityGridFilters";
import {clusterMemberListParamsFromFilterModel} from "./components/rawTable/supplierClusterMemberGridFilters";
import {SupplierNormalizedTable} from "./components/normalizedTable/SupplierNormalizedTable";
import {SupplierRawTable} from "./components/rawTable/SupplierRawTable";
import {SUPPLIER_RAW_ORDERING_FIELDS} from "./components/rawTable/SupplierRawColumns";
import {useSupplierEntitiesQuery} from "./hooks/useSupplierEntitiesQuery";
import {useSupplierClusterMembersQuery} from "./hooks/useSupplierClusterMembersQuery";
import {useSupplierRegistrySummaryQuery} from "./hooks/useSupplierRegistrySummaryQuery";
import {DRAWER_HEADER_HEIGHT_LG} from "~/components/main/drawer/DrawerConstants.ts";
import {PageHeader} from "~/components/main/header/PageHeader.tsx";

const VIEW_MODES = ['Normalized view', 'Raw view'] as const;
/** Highest spend first — both views sort by `spend` server-side (§2.1, §2.2). */
const INITIAL_SORT_MODEL = [{field: 'spend', sort: 'desc'}] as const;
const NO_VALUE = '—';

export const SupplierRegistryPage: React.FC = observer(() => {
    const {bagStore} = useStores();

    const navigate = useNavigate();
    const {id:databagId} = useParams<DatabagRouteMatch>();

    const [mode, setMode] = useState<number>(0);
    const isRaw = mode === 1;

    const {input: searchField, debounced: debouncedSearch, setInput: setSearchValue} = useDebouncedSearch();

    const normalizedGrid = useDataGridQueryState({
        initialSortModel: [...INITIAL_SORT_MODEL],
        resetPageOn: debouncedSearch,
    });
    const rawGrid = useDataGridQueryState({
        initialSortModel: [...INITIAL_SORT_MODEL],
        resetPageOn: debouncedSearch,
        orderingFields: SUPPLIER_RAW_ORDERING_FIELDS,
    });
    const normalizedColumnFilters = entityListParamsFromFilterModel(normalizedGrid.filterModel);
    const rawColumnFilters = clusterMemberListParamsFromFilterModel(rawGrid.filterModel);

    const {data: normalizedData, isFetching} = useSupplierEntitiesQuery(bagStore.bagId, {
        // No status filter: the Normalized view lists every resolved supplier, matching the
        // "Suppliers" pill's own confirmed + needs_review count (§2.25).
        ...normalizedColumnFilters,
        search: debouncedSearch || undefined,
        ordering: normalizedGrid.ordering,
        page: normalizedGrid.page,
        pageSize: normalizedGrid.pageSize,
    });

    const {data: summary, isLoading: isSummaryLoading} = useSupplierRegistrySummaryQuery(bagStore.bagId);

    // Raw view only. Nothing outside the Raw table reads these rows any more — the KPI strip's
    // counts come from `registry_summary` (§2.25) — so on the Normalized view this is dead weight.
    const {data: rawData, isFetching: isRawFetching, isError: isRawError} = useSupplierClusterMembersQuery(
        bagStore.bagId, {
            ...rawColumnFilters,
            search: debouncedSearch || undefined,
            ordering: rawGrid.ordering,
            page: rawGrid.page,
            pageSize: rawGrid.pageSize,
        },
        {enabled: isRaw},
    );

    // The query only starts once Raw view is opened, so there is one render where it is enabled
    // but not yet fetching — count "no data and no error" as loading so the grid never flashes
    // its empty state on the way in.
    const isRawLoading = isRawFetching || (!rawData && !isRawError);

    // Stable so the Raw table's memoised columns aren't rebuilt on every render.
    const openEntity = useCallback((entityId: number) => {
        const base = generatePath(routes.supplier_360, {id: databagId ?? String(bagStore.bagId)});
        navigate(`${base}/${entityId}/overview`);
    }, [databagId, bagStore.bagId, navigate]);

    return (
        <Stack gap={2} sx={{height: `calc(100vh - ${DRAWER_HEADER_HEIGHT_LG}px - 32px)`}}>
            {/* Title lives in the app's top bar (Mithra 3.1 header) */}
            <PageHeader title="Suppliers 360"/>
            {isSummaryLoading && <Stack direction="row"  gap={2}>
                <Skeleton variant="rounded" width={150} height={36} />
                <Skeleton variant="rounded" width={150} height={36} />
                <Skeleton variant="rounded" width={150} height={36} />
                <Skeleton variant="rounded" width={150} height={36} />
            </Stack>}
            {summary && <Stack direction="row" flexWrap="wrap" useFlexGap gap="10px">
                <StatChip
                    icon={{element: <StoreIcon />, color: COLORS.info.main}}
                    title="Suppliers"
                    value={summary?.suppliers ?? NO_VALUE}
                />
                <StatChip
                    icon={{element: <DollarCoinIcon/>, color: COLORS.success.main}}
                    title="Spend"
                    value={summary ? <CurrencyComponent v={summary.spend}/> : NO_VALUE}
                />
                <StatChip
                    icon={{element: <DbIcon/>, color: COLORS.secondary.main}}
                    title="Raw rows"
                    value={summary?.raw_rows ?? NO_VALUE}
                    description={summary ? `${summary.sources} ${summary.sources === 1 ? 'source' : 'sources'}` : undefined}
                />
                {/* <StatChip
                    icon={{element: <VerifiedIcon/>, color: COLORS.success.main}}
                    title="Resolved"
                    // `resolved_pct` is null (not 0) when no entity is awaiting review at all.
                    value={summary?.resolved_pct != null ? `${summary.resolved_pct}%` : NO_VALUE}
                    description={summary && summary.resolved_total > 0
                        ? `${summary.resolved_confirmed} of ${summary.resolved_total}`
                        : undefined}
                /> */}
            </Stack>}
            <Stack direction="row" alignItems="center"  gap={2}>
                <TextField
                    size="small"
                    placeholder={isRaw ? 'Search by raw name' : 'Search by name'}
                    value={searchField}
                    onChange={e => setSearchValue(e.target.value)}
                    sx={{
                        width: 240,
                        height: 33,
                        '& input': {py: '5px'},
                        '& .MuiInputBase-root': {pl: 1},
                        '& .MuiInputBase-input::placeholder': {
                            color: 'text.primary',
                            opacity: 1,
                        },
                    }}
                    slotProps={{
                        input: {
                            startAdornment: <InputAdornment position="start" sx={{mr: 0.5}}>
                                <SearchIcon aria-hidden color={COLORS.black[700]}/>
                            </InputAdornment>,
                            endAdornment: searchField
                                ? <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label="clear search"
                                        onClick={() => setSearchValue('')}>
                                        <CloseIcon width={16} height={16}/>
                                    </IconButton>
                                </InputAdornment>
                                : undefined,
                        },
                    }}
                />
                <SegmentedControl
                    labels={[...VIEW_MODES]}
                    value={mode}
                    onChange={m => setMode(m)}
                />
            </Stack>
            {isRaw
                ? <SupplierRawTable
                            rows={rawData?.results ?? []}
                            isLoading={isRawLoading}
                            rowCount={rawData?.count ?? 0}
                            gridState={rawGrid}
                            isSearchActive={Boolean(debouncedSearch)}
                            onOpenEntity={openEntity}
                            title={`${rawData?.count ?? 0} raw rows`}
                />
                : <SupplierNormalizedTable
                            rows={normalizedData?.results ?? []}
                            isLoading={isFetching}
                            rowCount={normalizedData?.count ?? 0}
                            gridState={normalizedGrid}
                            isSearchActive={Boolean(debouncedSearch)}
                            onOpenEntity={openEntity}
                            title={`${normalizedData?.count ?? 0} suppliers`}
                />}
        </Stack>
    );
});
